import { supabase } from "@/integrations/supabase/client";
import { DisplaySong } from "@/types/displayHistory";

function isValidUUID(uuid: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(uuid);
}

// Fetch songs for a specific year
export const fetchYearSongs = async (yearId: string): Promise<DisplaySong[]> => {
  if (!isValidUUID(yearId)) {
    console.warn('fetchYearSongs: yearId is not a valid UUID:', yearId);
    return [];
  }
  const { data, error } = await supabase
    .from('display_songs')
    .select('*')
    .eq('display_year_id', yearId)
    .order('title');
  
  if (error) {
    console.error('Error fetching year songs:', error);
    return [];
  }
  
  // For each song, fetch the linked sequence (if any)
  const songsWithSequence = await Promise.all(
    (data || []).map(async (song) => {
      let sequence = null;
      if (song.display_id) {
        // Use the new RPC function to get sequences for the display
        const { data: sequences } = await supabase
          .rpc('get_display_sequences', { p_display_id: song.display_id });
        
        if (sequences && sequences.length > 0) {
          // Try to find a sequence that matches this song by name similarity
          sequence = sequences.find(seq => 
            seq.sequence_name?.toLowerCase().includes(song.title?.toLowerCase() || '') ||
            song.title?.toLowerCase().includes(seq.sequence_name?.toLowerCase() || '')
          ) || sequences[0]; // Fall back to first sequence if no name match
        }
      }
      if (song.reused_from) {
        const { data: originalSong } = await supabase
          .from('display_songs')
          .select('*')
          .eq('id', song.reused_from)
          .single();
          
        if (originalSong) {
          return { ...song, originalSong };
        }
      }
      return { ...song, sequence };
    })
  );
  
  return songsWithSequence;
};

// Mock data for development (will be replaced with Supabase data)
export const mockDisplaySongs: { [key: string]: DisplaySong[] } = {
  '1': [
    {
      id: '1',
      display_year_id: '1',
      title: 'Jingle Bells',
      artist: 'Traditional',
      year_introduced: 2020,
      reused_from: null,
      sequence_file_url: null,
      sequence_available: false,
      sequence_price: null,
      created_at: '2023-01-01T00:00:00Z',
      updated_at: '2023-01-01T00:00:00Z'
    }
  ],
  '2': [
    {
      id: '2',
      display_year_id: '2',
      title: 'Carol of the Bells',
      artist: 'Trans-Siberian Orchestra',
      year_introduced: 2021,
      reused_from: null,
      sequence_file_url: null,
      sequence_available: true,
      sequence_price: 19.99,
      created_at: '2023-01-01T00:00:00Z',
      updated_at: '2023-01-01T00:00:00Z'
    }
  ],
  '3': [
    {
      id: '3',
      display_year_id: '3',
      title: 'All I Want for Christmas Is You',
      artist: 'Mariah Carey',
      year_introduced: 2022,
      reused_from: null,
      sequence_file_url: null,
      sequence_available: true,
      sequence_price: 24.99,
      created_at: '2023-01-01T00:00:00Z',
      updated_at: '2023-01-01T00:00:00Z'
    },
    {
      id: '4',
      display_year_id: '3',
      title: 'Let It Go',
      artist: 'Idina Menzel',
      year_introduced: 2022,
      reused_from: null,
      sequence_file_url: null,
      sequence_available: true,
      sequence_price: 29.99,
      created_at: '2023-01-01T00:00:00Z',
      updated_at: '2023-01-01T00:00:00Z'
    }
  ],
  '4': [
    {
      id: '5',
      display_year_id: '4',
      title: 'Carol of the Bells',
      artist: 'Trans-Siberian Orchestra',
      year_introduced: 2021,
      reused_from: '2',
      sequence_file_url: null,
      sequence_available: true,
      sequence_price: 19.99,
      created_at: '2023-01-01T00:00:00Z',
      updated_at: '2023-01-01T00:00:00Z',
      original_song: {
        id: '2',
        display_year_id: '2',
        title: 'Carol of the Bells',
        artist: 'Trans-Siberian Orchestra',
        year_introduced: 2021,
        reused_from: null,
        sequence_file_url: null,
        sequence_available: true,
        sequence_price: 19.99,
        created_at: '2023-01-01T00:00:00Z',
        updated_at: '2023-01-01T00:00:00Z'
      }
    },
    {
      id: '6',
      display_year_id: '4',
      title: 'All I Want for Christmas Is You',
      artist: 'Mariah Carey',
      year_introduced: 2022,
      reused_from: '3',
      sequence_file_url: null,
      sequence_available: true,
      sequence_price: 24.99,
      created_at: '2023-01-01T00:00:00Z',
      updated_at: '2023-01-01T00:00:00Z',
      original_song: {
        id: '3',
        display_year_id: '3',
        title: 'All I Want for Christmas Is You',
        artist: 'Mariah Carey',
        year_introduced: 2022,
        reused_from: null,
        sequence_file_url: null,
        sequence_available: true,
        sequence_price: 24.99,
        created_at: '2023-01-01T00:00:00Z',
        updated_at: '2023-01-01T00:00:00Z'
      }
    },
    {
      id: '7',
      display_year_id: '4',
      title: 'Christmas Eve/Sarajevo',
      artist: 'Trans-Siberian Orchestra',
      year_introduced: 2023,
      reused_from: null,
      sequence_file_url: null,
      sequence_available: true,
      sequence_price: 34.99,
      created_at: '2023-01-01T00:00:00Z',
      updated_at: '2023-01-01T00:00:00Z'
    }
  ]
};
